import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Upload, X, Loader2, Check, AlertCircle } from 'lucide-react';
import { useToast } from "../hooks/use-toast";
import { Alert, AlertDescription } from "./ui/alert";
import { API_BASE_URL } from '../config';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Interfaces removed - using plain JavaScript objects

// Keep the same helper function for WebP conversion
const convertToWebP = async (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const MAX_SIZE = 2160;
      
      // Calculate new dimensions maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      
      if (width > MAX_SIZE || height > MAX_SIZE) {
        if (width > height) {
          height = Math.round((height * MAX_SIZE) / width);
          width = MAX_SIZE;
        } else {
          width = Math.round((width * MAX_SIZE) / height);
          height = MAX_SIZE;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d', {
        alpha: true,
        willReadFrequently: true
      });
      
      // Clear the canvas to ensure clean alpha channel
      ctx?.clearRect(0, 0, width, height);
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name.replace(/\.[^/.]+$/, '.webp'), {
            type: 'image/webp'
          }));
        }
      }, 'image/webp', {
        quality: 0.8,
        alpha: true
      });
    };
    img.src = URL.createObjectURL(file);
  });
};

// Modified Sortable Image Component for dnd-kit
const SortableImageItem = memo(({ image, onRemove, onPreview, isUploading, hasError }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`relative bg-gray-50 rounded-lg p-2 touch-none cursor-grab active:cursor-grabbing group
        ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium z-10">
        {image.order}
      </div>
      
      <div 
        className="relative aspect-square w-full mb-2 overflow-hidden rounded-md"
        onClick={(e) => {
          e.stopPropagation();
          onPreview(image);
        }}
      >
        <img
          src={image.url || image.preview}
          alt={`Preview ${image.order}`}
          className="w-full h-full object-cover cursor-pointer transition-transform duration-200 group-hover:scale-105"
        />
        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}
        {hasError && (
          <div className="absolute top-2 right-2">
            <div className="bg-red-500 rounded-full p-1">
              <AlertCircle className="h-3 w-3 text-white" />
            </div>
          </div>
        )}
        {image.url && (
          <div className="absolute top-2 right-2">
            <div className="bg-green-500 rounded-full p-1">
              <Check className="h-3 w-3 text-white" />
            </div>
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex-1 px-2 truncate text-xs text-gray-600">
          {image.name || image.file?.name}
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(image.id);
          }}
          className="p-1 hover:bg-gray-200 rounded cursor-pointer touch-none"
          {...{
            // Remove drag handlers for this button
            ...Object.fromEntries(
              Object.entries(listeners).map(([key]) => [key, (e) => e.stopPropagation()])
            )
          }}
        >
          <X className="h-3 w-3 text-gray-500" />
        </button>
      </div>
    </div>
  );
});

// Keep the same Modal and Upload Card Components
const ImagePreviewModal = memo(({ image, onClose }) => {
  if (!image) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full h-full sm:h-auto sm:max-h-[90vh] bg-white rounded-lg overflow-hidden flex flex-col sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw]">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-sm font-medium text-gray-700 truncate">
            {image.name || image.file?.name}
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="relative flex-1 overflow-auto p-2 sm:p-4 flex items-center justify-center bg-gray-50">
          <img
            src={image.url || image.preview}
            alt={image.name || image.file?.name}
            className="max-w-full max-h-[calc(90vh-8rem)] object-contain rounded-lg"
          />
        </div>
      </div>
    </div>
  );
});

const UploadCard = memo(({ onClick }) => (
  <div
    onClick={onClick}
    className="relative bg-white rounded-lg p-2 border-2 border-dashed border-gray-200 aspect-square flex flex-col items-center justify-center gap-2 hover:border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer group"
  >
    <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-white transition-colors">
      <Upload className="h-6 w-6 text-gray-400" />
    </div>
    <div className="text-center">
      <p className="text-sm text-gray-600">Add Images</p>
      <p className="text-xs text-gray-400">PNG, JPG, GIF</p>
    </div>
  </div>
));

// Modified Main ImageUpload Component
const ImageUpload = ({
  initialImages = [],
  onImagesUpdate,
  uploadEndpoint = '/api/image/upload',
  maxImages = 10
}) => {
  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(new Set());
  const [failedUploads, setFailedUploads] = useState(new Set());
  const [isUploadConfirmed, setIsUploadConfirmed] = useState(false);

  // Setup DND sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

    // Add this useEffect in ImageUpload
  useEffect(() => {
    console.log('Images state changed:', images);
    if (images.length > 0 && onImagesUpdate) {
        onImagesUpdate(images);
    }
  }, [images, onImagesUpdate]);

  // Initialize images
  useEffect(() => {
    if (initialImages?.length > 0) {
      const newImages = initialImages.map((img, index) => ({
        ...img,
        id: img.id || Math.random().toString(36).substring(7),
        order: index + 1
      }));
      
      const hasChanged = JSON.stringify(newImages) !== JSON.stringify(images);
      if (hasChanged) {
        setImages(newImages);
        console.log('Initial images loaded:', newImages);
      }
    }
  }, [initialImages]);

  // Log images state whenever it changes
  useEffect(() => {
    console.log('Current images state:', images);
  }, [images]);

  // Keep the same image processing and upload handlers
  const handleUpload = useCallback(async (image) => {
    try {
      if (!image.file) return { id: image.id, success: false, error: new Error('No file') };

      const fileToUpload = await convertToWebP(image.file);
      const formData = new FormData();
      formData.append('image', fileToUpload);
      formData.append('order', String(image.order));
      
      const response = await fetch(`${API_BASE_URL}${uploadEndpoint}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      
      return { id: image.id, url: data.url, success: true };
    } catch (error) {
      return { id: image.id, success: false, error };
    }
  }, [uploadEndpoint]);

  // Modified drag end handler for dnd-kit
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
  
    if (over && active.id !== over.id) {
      setImages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
  
        const newItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          order: index + 1
        }));
  
        // Log the updated order
        console.log('Images after reorder:', newItems);
  
        if (onImagesUpdate) {
          onImagesUpdate(newItems);
        }
  
        return newItems;
      });
    }
  }, [onImagesUpdate]);


  // Keep other handlers and effects the same
  const handleConfirmUpload = useCallback(async () => {
    const imagesToUpload = images.filter(img => !img.url);
    if (!imagesToUpload.length) {
        console.log('No images to upload');
        return;
    }

    console.log('Starting upload for images:', imagesToUpload);
    setUploadingImages(new Set(imagesToUpload.map(img => img.id)));
    
    try {
        const uploadResults = await Promise.all(imagesToUpload.map(handleUpload));
        console.log('Upload results:', uploadResults);
        
        setImages(prev => {
            const updatedImages = prev.map(img => {
                const result = uploadResults.find(r => r.id === img.id);
                if (result?.success) {
                    console.log(`Setting URL for image ${img.id}:`, result.url);
                    return { ...img, url: result.url };
                }
                return img;
            });
            
            console.log('Updated images with URLs:', updatedImages);
            
            // Important: Call onImagesUpdate with the updated images
            onImagesUpdate?.(updatedImages);
            
            return updatedImages;
        });
        
        const failures = uploadResults.filter(r => !r.success);
        setFailedUploads(new Set(failures.map(f => f.id)));
        
        const successCount = uploadResults.filter(r => r.success).length;
        if (successCount > 0) {
          
            toast({
                title: "Success",
                description: `Successfully uploaded ${successCount} image${successCount > 1 ? 's' : ''}`,
            });
        }
    } catch (error) {
        console.error('Upload error:', error);
        toast({
            variant: "destructive",
            title: "Upload Failed",
            description: "Failed to upload images. Please try again.",
        });
    } finally {
        setUploadingImages(new Set());
    }
}, [images, handleUpload, onImagesUpdate, toast]);

  const handleRemoveImage = useCallback((id: string) => {
    setImages(prev => {
      const newImages = prev.filter(img => img.id !== id).map((img, index) => ({
        ...img,
        order: index + 1
      }));
      
      // Log the updated images after deletion
      console.log('Images after deletion:', newImages);
      
      if (onImagesUpdate) {
        onImagesUpdate(newImages);
      }
      
      return newImages;
    });
  }, [onImagesUpdate]);

  const processNewImages = useCallback((files: File[]) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const remainingSlots = maxImages - images.length;
    const filesToAdd = imageFiles.slice(0, remainingSlots);
  
    Promise.all(
      filesToAdd.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            id: Math.random().toString(36).substring(7),
            file,
            preview: reader.result,
            order: images.length + 1
          });
        };
        reader.readAsDataURL(file);
      }))
    ).then(newImages => {
      setImages(prev => {
        const updatedImages = [...prev, ...newImages].map((img, index) => ({
          ...img,
          order: index + 1
        }));
        
        if (onImagesUpdate) {
          onImagesUpdate(updatedImages);
        }
        
        return updatedImages;
      });
    });
  }, [images.length, maxImages, onImagesUpdate]);

  const handleImageChange = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    processNewImages(files);
  }, [processNewImages]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    processNewImages(files);
  }, [processNewImages]);

  // Compute derived values
  const hasUnsavedImages = useMemo(() => images.some(img => !img.url), [images]);

  return (
    <div className="w-full mx-auto p-4">
      <div className="w-full bg-white rounded-lg shadow-sm p-6">
        <div onDragOver={handleDragOver} onDrop={handleDrop} className="w-full"> {/* Added w-full */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 auto-rows-auto"> {/* Added w-full and auto-rows-auto */}
              <SortableContext
                items={images.map(img => img.id)}
                strategy={horizontalListSortingStrategy}
              >
                {images.map((image) => (
                  <SortableImageItem
                    key={image.id}
                    image={image}
                    onRemove={handleRemoveImage}
                    onPreview={setPreviewImage}
                    isUploading={uploadingImages.has(image.id)}
                    hasError={failedUploads.has(image.id)}
                  />
                ))}
              </SortableContext>
  
              {images.length < maxImages && (
                <div className="w-full"> {/* Added w-full */}
                  <input
                    type="file"
                    id="imageInput"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <UploadCard 
                    onClick={() => document.getElementById('imageInput')?.click()}
                  />
                </div>
              )}
            </div>
          </DndContext>

          {/* Upload confirmation section */}
          {hasUnsavedImages && (
            <div className="mt-4 space-y-4">
              {failedUploads.size > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Some images failed to upload. Please try again.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="flex justify-end">
                <button
                  onClick={handleConfirmUpload}
                  disabled={uploadingImages.size > 0}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingImages.size > 0 && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Confirm Upload
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image preview modal */}
      {previewImage && (
        <ImagePreviewModal 
          image={previewImage} 
          onClose={() => setPreviewImage(null)} 
        />
      )}
    </div>
  );
};

export { ImageUpload };
export default ImageUpload;